/**
 * Email-forward inbox page.
 *
 * Shows the user's forwarded travel emails as a table with status badges,
 * "Re-parse" and "Assign to trip" actions. When the `email_inbox` feature
 * is disabled, the page renders a banner explaining how to re-enable it
 * and does NOT surface the reparse/assign controls.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Inbox, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useTravelInbox, useTravelTrips, useTravelFeature } from '../hooks';
import type { TravelInboundEmailResponse, TravelInboundEmailStatus } from '../api';

const STATUS_LABELS: Record<TravelInboundEmailStatus, string> = {
  pending: 'Pending',
  ok: 'Applied',
  error: 'Error',
  unassigned: 'Unassigned',
};

const STATUS_CLASS: Record<TravelInboundEmailStatus, string> = {
  pending: 'bg-muted text-muted-foreground',
  ok: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  error: 'bg-destructive/15 text-destructive',
  unassigned: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
};

function StatusBadge({ status }: { status: TravelInboundEmailStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_CLASS[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

function formatReceivedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function EmailRow({
  email,
  onReparse,
  onAssign,
  reparsing,
  assigning,
}: {
  email: TravelInboundEmailResponse;
  onReparse: (emailId: string) => void;
  onAssign: (emailId: string, tripId: string) => void;
  reparsing: boolean;
  assigning: boolean;
}) {
  const { trips } = useTravelTrips();
  const [selectedTripId, setSelectedTripId] = useState('');

  return (
    <Card>
      <CardContent className="py-3 space-y-2">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={email.parseStatus} />
              <span className="text-xs text-muted-foreground">
                {formatReceivedAt(email.receivedAt)}
              </span>
            </div>
            <div className="mt-1 font-medium truncate">{email.subject || '(no subject)'}</div>
            {email.fromAddress && (
              <div className="text-xs text-muted-foreground truncate">{email.fromAddress}</div>
            )}
            {email.parseResult.summary && (
              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                {email.parseResult.summary}
              </p>
            )}
            {email.error && (
              <p className="mt-1 text-xs text-destructive line-clamp-2">{email.error}</p>
            )}
            {email.parseResult.reason && !email.error && (
              <p className="mt-1 text-xs text-muted-foreground italic">
                {email.parseResult.reason}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={selectedTripId} onValueChange={setSelectedTripId}>
            <SelectTrigger className="h-8 w-48">
              <SelectValue placeholder="Assign to trip…" />
            </SelectTrigger>
            <SelectContent>
              {trips.map((t) => (
                <SelectItem key={t.tripId} value={t.tripId}>
                  {t.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="secondary"
            disabled={!selectedTripId || assigning}
            onClick={() => {
              if (selectedTripId) onAssign(email.emailId, selectedTripId);
            }}
          >
            Apply to trip
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={reparsing}
            onClick={() => onReparse(email.emailId)}
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${reparsing ? 'animate-spin' : ''}`} />
            Re-parse
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function TravelInboxPage() {
  const navigate = useNavigate();
  const feature = useTravelFeature('email_inbox');
  const { emails, featureEnabled, loading, reparse, assignToTrip, isReparsing, isAssigning } =
    useTravelInbox();

  // Prefer the hook's derived state (feature toggle may be updated without a
  // refetch), but fall back to the list response's `featureEnabled` so the
  // UI is right on first load.
  const effectivelyEnabled = feature.enabled && featureEnabled;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/a/arche_travel')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Mail className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-lg font-semibold">Email inbox</h1>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-3xl mx-auto space-y-4">
          {!effectivelyEnabled && (
            <Card>
              <CardContent className="py-4 text-sm space-y-2">
                <div className="font-medium">Email forwarding is disabled</div>
                <p className="text-muted-foreground">
                  Turn it back on in Settings → Email Forwarding. While off, any new forwarded
                  emails are recorded as <em>Unassigned</em> and never parsed.
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate('/a/arche_travel/settings')}
                >
                  Go to settings
                </Button>
              </CardContent>
            </Card>
          )}

          {loading ? (
            <>
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </>
          ) : emails.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Inbox className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No forwarded emails yet.</p>
              <p className="text-xs mt-1">
                Forward a booking confirmation to the address shown in Settings → Email Forwarding.
              </p>
            </div>
          ) : (
            emails.map((email) => (
              <EmailRow
                key={email.emailId}
                email={email}
                onReparse={(id) => {
                  void reparse(id);
                }}
                onAssign={(id, tripId) => {
                  void assignToTrip(id, tripId);
                }}
                reparsing={isReparsing}
                assigning={isAssigning}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
